import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Zap, Users, BarChart2, Shield, Building2, Puzzle, Globe, ChevronRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import {
  wsniperIcon,
  wscannerIcon,
  wtraderIcon,
  wpanelIcon,
  wclaimerIcon,
  wcheckerIcon,
  wbridgeIcon,
  whatakitIcon,
} from '../assets';
import { EXTERNAL_LINKS } from '../config';
import './SplitAudience.css';

const CONTENT = {
  en: {
    eyebrow: 'Built for everyone',
    title: { accent: 'Two paths,', rest: 'one ecosystem.' },
    subtitle: "Whether you manage or acquire digital collectibles or build the next Web3 product, we have the tools you need to take it to another level.",
    retail: {
      tag: 'For You',
      headline: 'Your edge in\nthe markets.',
      sub: 'Trade smarter. Verify faster. Own your on-chain experience.',
      points: [
        { icon: <Zap size={15} />,      text: 'Snipe rare NFTs in milliseconds' },
        { icon: <BarChart2 size={15} />, text: 'Track wallets & market moves live' },
        { icon: <Users size={15} />,    text: 'Get roles in your favourite communities' },
        { icon: <Shield size={15} />,   text: 'Verify holdings without leaving Discord' },
      ],
      tools: [wsniperIcon, wscannerIcon, wtraderIcon, wpanelIcon],
      toolNames: ['W·Sniper', 'W·Scanner', 'W·Trader', 'W·Panel'],
      cta: 'Explore tools',
      ctaHref: EXTERNAL_LINKS.TOOLS_PLATFORM,
      ctaSecondary: 'Get WhataKit',
      ctaSecondaryHref: '#ecosystem',
      video: '/videos/tools-demo.mov',
    },
    enterprise: {
      tag: 'For Your Business',
      headline: 'Web3\ninfrastructure\nthat scales.',
      sub: 'Plug modular Web3 tools into your stack. Zero complexity, real results.',
      points: [
        { icon: <Puzzle size={15} />,    text: 'Plug-and-play role & access management' },
        { icon: <Globe size={15} />,     text: 'Cross-chain bridging for your users' },
        { icon: <Users size={15} />,     text: 'Automated token distribution at scale' },
        { icon: <Building2 size={15} />, text: 'White-label ready · API access' },
      ],
      tools: [wpanelIcon, wclaimerIcon, wcheckerIcon, wbridgeIcon],
      toolNames: ['W·Panel', 'W·Claimer', 'W·Checker', 'W·Bridge'],
      cta: 'Contact us',
      ctaHref: `mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'info@whatalab.io'}`,
      ctaSecondary: 'See ecosystem',
      ctaSecondaryHref: '#ecosystem',
      image: '/screenshots/wpanel-welcome.png',
    },
  },
  es: {
    eyebrow: 'Construido para todos',
    title: { accent: 'Dos caminos,', rest: 'un ecosistema.' },
    subtitle: 'Ya sea que gestiones o adquieras colectibles digitales o construyas el próximo producto Web3, tenemos las herramientas que necesitas usar para elevarlo a otro nivel.',
    retail: {
      tag: 'Para Ti',
      headline: 'Tu ventaja en\nlos mercados.',
      sub: 'Opera mejor. Verifica más rápido. Controla tu experiencia on-chain.',
      points: [
        { icon: <Zap size={15} />,      text: 'Snipea NFTs raros en milisegundos' },
        { icon: <BarChart2 size={15} />, text: 'Rastrea wallets y movimientos en vivo' },
        { icon: <Users size={15} />,    text: 'Obtén roles en tus comunidades favoritas' },
        { icon: <Shield size={15} />,   text: 'Verifica holdings sin salir de Discord' },
      ],
      tools: [wsniperIcon, wscannerIcon, wtraderIcon, wpanelIcon],
      toolNames: ['W·Sniper', 'W·Scanner', 'W·Trader', 'W·Panel'],
      cta: 'Explorar herramientas',
      ctaHref: EXTERNAL_LINKS.TOOLS_PLATFORM,
      ctaSecondary: 'Obtener WhataKit',
      ctaSecondaryHref: '#ecosystem',
      video: '/videos/tools-demo.mov',
    },
    enterprise: {
      tag: 'Para Tu Negocio',
      headline: 'Infraestructura\nWeb3 que\nescala.',
      sub: 'Integra herramientas Web3 modulares en tu stack. Sin complejidad, con resultados.',
      points: [
        { icon: <Puzzle size={15} />,    text: 'Gestión de roles y accesos plug-and-play' },
        { icon: <Globe size={15} />,     text: 'Bridge cross-chain para tus usuarios' },
        { icon: <Users size={15} />,     text: 'Distribución automatizada de tokens' },
        { icon: <Building2 size={15} />, text: 'White-label · Acceso API' },
      ],
      tools: [wpanelIcon, wclaimerIcon, wcheckerIcon, wbridgeIcon],
      toolNames: ['W·Panel', 'W·Claimer', 'W·Checker', 'W·Bridge'],
      cta: 'Contactar',
      ctaHref: `mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'info@whatalab.io'}`,
      ctaSecondary: 'Ver ecosistema',
      ctaSecondaryHref: '#ecosystem',
      image: '/screenshots/wpanel-welcome.png',
    },
  },
};

const Panel = ({ side, content, active, onEnter, onLeave, isOtherActive }) => {
  const isRetail = side === 'retail';
  const hasMedia = !!content.image || !!content.video;

  return (
    <div
      className={`split-panel split-panel--${side} ${active ? 'split-panel--active' : ''} ${isOtherActive ? 'split-panel--shrunk' : ''}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      {/* Background layers */}
      <div className="split-panel__bg" />
      <div className="split-panel__noise" />
      <div className={`split-panel__glow split-panel__glow--${side}`} />

      {/* Divider hint — only visible on the border side */}
      {isRetail && <div className="split-divider" />}

      <div className={`split-panel__inner ${active && hasMedia ? 'split-panel__inner--with-image' : ''}`}>
        
        {/* Text column */}
        <div className="split-content">
          {/* Tag pill */}
          <motion.div
            className={`split-tag split-tag--${side}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {content.tag}
          </motion.div>

          {/* Headline */}
          <h2 className="split-headline">
            {content.headline.split('\n').map((line, i) => (
              <span key={i} className="split-headline__line">{line}</span>
            ))}
          </h2>

          {/* Sub — revealed on hover */}
          <AnimatePresence>
            {active && (
              <motion.p
                className="split-sub"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.08 }}
              >
                {content.sub}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Feature points — revealed on hover */}
          <AnimatePresence>
            {active && (
              <motion.ul
                className="split-points"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.12 }}
              >
                {content.points.map((point, i) => (
                  <motion.li
                    key={i}
                    className="split-point"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.12 + i * 0.06 }}
                  >
                    <span className={`split-point__icon split-point__icon--${side}`}>
                      {point.icon}
                    </span>
                    {point.text}
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>

          {/* CTAs — revealed on hover */}
          <AnimatePresence>
            {active && (
              <motion.div
                className="split-ctas"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.2 }}
              >
                <a
                  href={content.ctaHref}
                  target={content.ctaHref.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className={`split-cta split-cta--primary split-cta--${side}`}
                >
                  {content.cta}
                  <ArrowRight size={14} />
                </a>
                <a
                  href={content.ctaSecondaryHref}
                  className="split-cta split-cta--ghost"
                >
                  {content.ctaSecondary}
                  <ChevronRight size={14} />
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Media column (image or video) — revealed on hover */}
        <AnimatePresence>
          {active && hasMedia && (
            <motion.div
              className={`split-preview split-preview--${side}`}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              {content.video ? (
                <video
                  src={content.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  disablePictureInPicture
                  disableRemotePlayback
                />
              ) : (
                <img src={content.image} alt="Preview" loading="lazy" />
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>

      {/* Hover hint arrow — visible when NOT active */}
      <AnimatePresence>
        {!active && !isOtherActive && (
          <motion.div
            className={`split-hint split-hint--${side}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ChevronRight size={18} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SplitAudience = () => {
  const { language } = useLanguage();
  const [active, setActive] = useState(null); // 'retail' | 'enterprise' | null
  const content = CONTENT[language] || CONTENT.en;

  return (
    <section className="split-section section">
      <div className="container">
        {/* Header — mismo patrón que ProcessSteps */}
        <motion.div
          className="ps-header split-header"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.35 }}
        >
          <div className="ps-eyebrow">
            <span className="ps-eyebrow-dot" />
            {content.eyebrow}
          </div>
          <div className="split-header-row">
            <h2 className="ps-title">
              <span className="ps-title-accent">{content.title.accent}</span>
              <br />
              {content.title.rest}
            </h2>
            <p className="split-subtitle">{content.subtitle}</p>
          </div>
        </motion.div>

        <div className="split-container">
        <Panel
          side="retail"
          content={content.retail}
          active={active === 'retail'}
          isOtherActive={active === 'enterprise'}
          onEnter={() => setActive('retail')}
          onLeave={() => setActive(null)}
        />
        <Panel
          side="enterprise"
          content={content.enterprise}
          active={active === 'enterprise'}
          isOtherActive={active === 'retail'}
          onEnter={() => setActive('enterprise')}
          onLeave={() => setActive(null)}
        />
        </div>
      </div>
    </section>
  );
};

export default SplitAudience;
